import { useEffect, useRef, useState } from 'react';
import {
  ArrowDownToLine,
  ArrowRightToLine,
  Smartphone,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import { TableKit } from '@tiptap/extension-table';
import StarterKit from '@tiptap/starter-kit';

import { Button } from '@/components/Button/Button';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { TableUnit } from '@/features/content/TableUnit';
import { applyTableHeaderRow, countTableColumns } from '@/features/content/tableUnitHtml';

import {
  buildTableHtmlFromGrid,
  isMultiCellGrid,
  parsePastedTable,
} from './pastedTable';
import type { EditableContentUnit } from './editorTypes';
import styles from './TableUnitEditor.module.css';

interface TableUnitEditorProps {
  caption?: string;
  onChange: (updates: Partial<EditableContentUnit>) => void;
  tableDensity?: EditableContentUnit['tableDensity'];
  tableHeaderRow?: boolean;
  tableHtml?: string;
  tableStickyHeader?: boolean;
  tableStyle?: EditableContentUnit['tableStyle'];
}

// Past this, a table needs enough horizontal scrolling on a phone that a student
// cannot see a row's label and its value at the same time. Chosen from a 375px
// viewport against the 5rem minimum cell width the renderer sets.
const CROWDED_COLUMN_COUNT = 5;

// A phone viewport, so the preview is the real thing at the real width.
const PHONE_PREVIEW_WIDTH = 375;

const STARTER_TABLE_HTML =
  '<table><thead><tr>' +
  '<th scope="col"><p></p></th><th scope="col"><p></p></th><th scope="col"><p></p></th>' +
  '</tr></thead><tbody>' +
  '<tr><td><p></p></td><td><p></p></td><td><p></p></td></tr>' +
  '<tr><td><p></p></td><td><p></p></td><td><p></p></td></tr>' +
  '</tbody></table>';

// The unit IS the table, so the document may hold nothing else. This is what
// makes the stored html trustworthy: there is no way to end up with a stray
// paragraph that the renderer would silently drop.
const TableOnlyDocument = Document.extend({ content: 'table' });

interface StructureButton {
  action: () => void;
  icon: LucideIcon;
  label: string;
}

export function TableUnitEditor({
  caption,
  onChange,
  tableDensity,
  tableHeaderRow = true,
  tableHtml,
  tableStickyHeader = false,
  tableStyle,
}: TableUnitEditorProps) {
  const [isPhonePreviewOpen, setIsPhonePreviewOpen] = useState(false);
  const initialHtmlRef = useRef(tableHtml?.trim() || STARTER_TABLE_HTML);

  const editor = useEditor({
    content: initialHtmlRef.current,
    editorProps: {
      // A name for the text box TipTap makes of the grid. These replace
      // TipTap's own attributes, so the role it adds is repeated here.
      attributes: {
        'aria-label': 'Table cells',
        'aria-multiline': 'true',
        role: 'textbox',
      },
      handlePaste: (_view, event) => {
        const clipboard = event.clipboardData;

        if (!clipboard) {
          return false;
        }

        const grid = parsePastedTable(
          clipboard.getData('text/html'),
          clipboard.getData('text/plain'),
        );

        if (!grid || !isMultiCellGrid(grid)) {
          // A single value goes into the cell the cursor is in, the way any
          // other paste would. Only a real grid replaces the table.
          return false;
        }

        event.preventDefault();
        // Replaces the table rather than nesting one inside a cell — which the
        // schema would otherwise allow, and which is never what anyone means.
        editorRef.current?.commands.setContent(buildTableHtmlFromGrid(grid), {
          emitUpdate: true,
        });
        return true;
      },
    },
    extensions: [
      StarterKit.configure({
        // The grid is the document: headings, lists, quotes, and code blocks
        // have no place inside a data cell and only complicate the markup.
        blockquote: false,
        bulletList: false,
        codeBlock: false,
        document: false,
        heading: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
      }),
      TableOnlyDocument,
      TableKit.configure({
        table: {
          // Tab out of the last cell appends a row, which is how every
          // spreadsheet behaves.
          allowTableNodeSelection: false,
          resizable: false,
        },
      }),
    ],
    immediatelyRender: false,
    onUpdate: ({ editor: currentEditor }) => {
      onChange({ tableHtml: currentEditor.getHTML() });
    },
  });

  const editorRef = useRef(editor);
  editorRef.current = editor;

  useEditorState({
    editor,
    selector: ({ transactionNumber }) => transactionNumber,
  });

  // A brand new unit stores no markup, so the starter grid the editor was seeded
  // with has to reach the draft — otherwise the instructor sees a table, saves,
  // and gets nothing.
  useEffect(() => {
    if (editor && !tableHtml?.trim()) {
      onChange({ tableHtml: editor.getHTML() });
    }
    // Deliberately once, on mount: this seeds the draft, it does not track it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) {
    return <div className={styles.loading}>Preparing table editor…</div>;
  }

  const chain = () => editor.chain().focus(undefined, { scrollIntoView: false });

  const rowButtons: StructureButton[] = [
    {
      action: () => chain().addRowAfter().run(),
      icon: ArrowDownToLine,
      label: 'Add row',
    },
    {
      action: () => chain().deleteRow().run(),
      icon: Trash2,
      label: 'Delete row',
    },
  ];

  const columnButtons: StructureButton[] = [
    {
      action: () => chain().addColumnAfter().run(),
      icon: ArrowRightToLine,
      label: 'Add column',
    },
    {
      action: () => chain().deleteColumn().run(),
      icon: Trash2,
      label: 'Delete column',
    },
  ];

  const currentHtml = tableHtml?.trim() || editor.getHTML();
  const columnCount = countTableColumns(currentHtml);
  const isCrowded = columnCount > CROWDED_COLUMN_COUNT;

  return (
    <div className={styles.root}>
      <p className={styles.hint}>
        Type in a cell and press Tab to move on — Tab in the last cell adds a
        row. You can also paste a table copied from a spreadsheet or document.
      </p>

      {/* Row and column controls are visible rather than hidden behind a menu:
          a professor should not have to discover them. */}
      <div className={styles.structureBar}>
        <div className={styles.structureGroup} role="group" aria-label="Rows">
          <span className={styles.structureLabel}>Rows</span>
          {rowButtons.map((button) => (
            <Tooltip content={button.label} key={button.label}>
              <Button
                aria-label={button.label}
                className={styles.structureButton}
                onClick={button.action}
                size="small"
                variant="ghost"
              >
                <button.icon aria-hidden="true" size={16} />
                <span className={styles.structureButtonText}>
                  {button.label.replace(' row', '')}
                </span>
              </Button>
            </Tooltip>
          ))}
        </div>

        <div className={styles.structureGroup} role="group" aria-label="Columns">
          <span className={styles.structureLabel}>Columns</span>
          {columnButtons.map((button) => (
            <Tooltip content={button.label} key={button.label}>
              <Button
                aria-label={button.label}
                className={styles.structureButton}
                onClick={button.action}
                size="small"
                variant="ghost"
              >
                <button.icon aria-hidden="true" size={16} />
                <span className={styles.structureButtonText}>
                  {button.label.replace(' column', '')}
                </span>
              </Button>
            </Tooltip>
          ))}
        </div>

        <Button
          aria-pressed={isPhonePreviewOpen}
          className={styles.previewToggle}
          onClick={() => {
            setIsPhonePreviewOpen((currentValue) => !currentValue);
          }}
          size="small"
          variant="ghost"
        >
          <Smartphone aria-hidden="true" size={16} />
          <span>Phone preview</span>
        </Button>
      </div>

      <EditorContent className={styles.editor} editor={editor} />

      {isCrowded ? (
        // A warning, never a block: a wide reference table can be the right
        // call, and the instructor is the one who knows.
        <p className={styles.warning} role="status">
          {columnCount} columns will be hard to read on a phone — students will
          have to scroll sideways. Consider splitting it into two tables.
        </p>
      ) : null}

      <div className={styles.settings}>
        <label className={styles.toggle}>
          <input
            checked={tableHeaderRow}
            onChange={(event) => {
              const headerRow = event.target.checked;
              const nextHtml = applyTableHeaderRow(
                editor.getHTML(),
                headerRow,
              );

              // Rewrite the markup as well as the flag, so the grid the
              // instructor is looking at matches what students will see.
              editor.commands.setContent(nextHtml, { emitUpdate: false });
              onChange({ tableHeaderRow: headerRow, tableHtml: nextHtml });
            }}
            type="checkbox"
          />
          <span>First row is a header</span>
        </label>

        <label className={styles.setting}>
          <span className={styles.settingLabel}>Style</span>
          <select
            className={styles.select}
            onChange={(event) => {
              onChange({
                tableStyle: event.target
                  .value as EditableContentUnit['tableStyle'],
              });
            }}
            value={tableStyle ?? 'plain'}
          >
            <option value="plain">Plain</option>
            <option value="striped">Striped rows</option>
            <option value="bordered">Bordered</option>
          </select>
        </label>

        <label className={styles.setting}>
          <span className={styles.settingLabel}>Spacing</span>
          <select
            className={styles.select}
            onChange={(event) => {
              onChange({
                tableDensity: event.target
                  .value as EditableContentUnit['tableDensity'],
              });
            }}
            value={tableDensity ?? 'comfortable'}
          >
            <option value="comfortable">Comfortable</option>
            <option value="compact">Compact</option>
          </select>
        </label>

        <label className={styles.toggle}>
          <input
            checked={tableStickyHeader}
            disabled={!tableHeaderRow}
            onChange={(event) => {
              onChange({ tableStickyHeader: event.target.checked });
            }}
            type="checkbox"
          />
          <span>Keep the header visible while scrolling</span>
        </label>
      </div>

      {isPhonePreviewOpen ? (
        <div className={styles.phonePreview}>
          <p className={styles.phonePreviewLabel}>
            As it looks on a phone ({PHONE_PREVIEW_WIDTH}px wide)
          </p>
          <div
            className={styles.phoneFrame}
            style={{ width: `${PHONE_PREVIEW_WIDTH}px` }}
          >
            {/* The real renderer at the real width — not an approximation, so
                what the instructor sees here is what students get. */}
            <TableUnit
              unit={{
                caption,
                collapseByDefault: false,
                density: tableDensity ?? 'comfortable',
                disableAutoNumbering: false,
                headerRow: tableHeaderRow,
                html: currentHtml,
                id: 'table-phone-preview',
                kind: 'table',
                stickyHeader: tableStickyHeader,
                style: tableStyle ?? 'plain',
                summaryLabel: 'Table preview',
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
